import React, { createContext, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { logEvent, Analytics } from 'firebase/analytics';
import { analytics } from '../firebase';

const COOKIE_CONSENT_KEY = 'cookie_consent_given';

interface AnalyticsContextType {
  trackEvent: (eventName: string, eventParams?: { [key: string]: any }) => void;
  trackPageView: (path: string) => void;
  hasConsent: () => boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const location = useLocation();
  const [analyticsInstance, setAnalyticsInstance] = React.useState<Analytics | null>(null);

  useEffect(() => {
    // Initialize Firebase Analytics
    analytics
      .then(instance => {
        setAnalyticsInstance(instance);
      })
      .catch(error => {
        console.error('[Analytics] Failed to initialize:', error);
      });
  }, []);

  const hasConsent = () => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    return consent === 'true';
  };

  useEffect(() => {
    // Track page views only if consent is given
    if (analyticsInstance && hasConsent()) {
      logEvent(analyticsInstance, 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }
  }, [location, analyticsInstance]);

  const trackEvent = (eventName: string, eventParams?: { [key: string]: any }) => {
    // Only track events if consent is given
    if (analyticsInstance && hasConsent()) {
      logEvent(analyticsInstance, eventName, eventParams);
    }
  };

  const trackPageView = (path: string) => {
    // Only track page views if consent is given
    if (analyticsInstance && hasConsent()) {
      logEvent(analyticsInstance, 'page_view', {
        page_path: path,
        page_title: document.title
      });
    }
  };

  return (
    <AnalyticsContext.Provider value={{ trackEvent, trackPageView, hasConsent }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}; 